import { useState, useEffect } from 'react';
import { fetchStudent } from '../../services/studentApi';
import type { StudentApiRecord } from '../../services/studentApi';
import './StudentDetail.css';
import { useTranslation } from 'react-i18next';

interface StudentDetailProps {
    studentId: string;
    onClose: () => void;
    onEdit?: (id: string) => void;
}

export default function StudentDetail({ studentId, onClose, onEdit }: StudentDetailProps) {
    const { t } = useTranslation();
    const [student, setStudent] = useState<StudentApiRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchStudent(studentId)
            .then((data) => setStudent(data))
            .catch((err) => setError(err.message || t('studentDetail.failedToLoad')))
            .finally(() => setLoading(false));
    }, [studentId]);

    /* ── Helpers ── */
    const dash = <span className="detail__muted">—</span>;

    const fmt = (val: string | null | undefined) =>
        val ? <>{val}</> : dash;

    const fmtDate = (val: string | null | undefined) =>
        val
            ? new Date(val).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
            : dash;

    const fmtGender = (g: string | null | undefined) =>
        g === 'M' ? t('common.male') : g === 'F' ? t('common.female') : g ? g : dash;

    const formatAddress = (u: StudentApiRecord['user'] | undefined | null) => {
        if (!u) return null;
        const parts = [u.address, u.city, u.state, u.postal_code, u.country].filter(Boolean);
        return parts.length ? parts.join(', ') : null;
    };

    /* ── Skeleton loader ── */
    if (loading) {
        return (
            <>
                <div className="detail__backdrop" onClick={onClose} />
                <aside className="detail" role="dialog" aria-label="Loading student profile">
                    <header className="detail__header">
                        <div className="detail__header-top">
                            <button className="detail__close" onClick={onClose} aria-label="Close panel">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="detail__skeleton detail__skeleton--avatar" />
                        <div className="detail__skeleton detail__skeleton--name" />
                        <div className="detail__skeleton detail__skeleton--badge" />
                    </header>
                    <div className="detail__body">
                        {[1, 2, 3].map((i) => (
                            <section className="detail__section" key={i}>
                                <div className="detail__skeleton detail__skeleton--title" />
                                <div className="detail__grid">
                                    {[1, 2, 3, 4].map((j) => (
                                        <div className="detail__field" key={j}>
                                            <div className="detail__skeleton detail__skeleton--label" />
                                            <div className="detail__skeleton detail__skeleton--value" />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                </aside>
            </>
        );
    }

    /* ── Error state ── */
    if (error || !student) {
        return (
            <>
                <div className="detail__backdrop" onClick={onClose} />
                <aside className="detail" role="dialog" aria-label="Error loading student">
                    <header className="detail__header">
                        <div className="detail__header-top">
                            <button className="detail__close" onClick={onClose} aria-label="Close panel">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    </header>
                    <div className="detail__body">
                        <div className="detail__error">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.4">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><circle cx="12" cy="16" r="1" fill="currentColor" />
                            </svg>
                            <p>{error || t('studentDetail.notFound')}</p>
                        </div>
                    </div>
                </aside>
            </>
        );
    }

    const s = student;
    // user is always present in the detail response; guard for type safety
    const u = s.user ?? { id: s.id, first_name: s.first_name ?? '', last_name: s.last_name ?? null, email: null, phone: null, date_of_birth: null, gender: null, address: null, city: null, state: null, postal_code: null, country: null, profile_photo_url: null, profile_photo_thumb_url: null };
    const fullName = s.name ?? `${u.first_name}${u.last_name ? ' ' + u.last_name : ''}`.trim();
    const statusLabels: Record<string, string> = {
        active: t('common.active'),
        inactive: t('common.inactive'),
        graduated: t('common.graduated'),
        transferred: t('common.transferred'),
        current: t('common.current'),
    };
    const statusLabel = statusLabels[s.enrollment_status] ?? s.enrollment_status.charAt(0).toUpperCase() + s.enrollment_status.slice(1);
    const address = formatAddress(u);
    const metaEntries = Object.entries(s.metadata || {});

    return (
        <>
            <div className="detail__backdrop" onClick={onClose} />

            <aside className="detail" role="dialog" aria-label={`Student profile: ${fullName}`}>
                {/* ── Header ── */}
                <header className="detail__header">
                    <div className="detail__header-top">
                        {onEdit && (
                            <button className="detail__edit" onClick={() => onEdit(s.id)} aria-label={t('common.edit')} title={t('common.edit')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                                </svg>
                                {t('common.edit')}
                            </button>
                        )}
                        <button className="detail__close" onClick={onClose} aria-label="Close panel" title="Close">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <div className="detail__avatar">
                        {u.profile_photo_thumb_url || u.profile_photo_url ? (
                            <img
                                src={u.profile_photo_thumb_url ?? u.profile_photo_url ?? ''}
                                alt={fullName}
                                className="detail__avatar-img"
                            />
                        ) : (
                            <span className="detail__avatar-initials">
                                {fullName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </span>
                        )}
                    </div>
                    <h2 className="detail__name">{fullName}</h2>
                    <span className={`detail__badge detail__badge--${s.enrollment_status}`}>{statusLabel}</span>
                    {s.school && <p className="detail__school-name">{s.school.name}</p>}
                </header>

                {/* ── Body ── */}
                <div className="detail__body">

                    {/* Academic */}
                    <section className="detail__section">
                        <h3 className="detail__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" />
                            </svg>
                            {t('studentDetail.academicInfo')}
                        </h3>
                        <div className="detail__grid">
                            <div className="detail__field">
                                <span className="detail__label">{t('studentDetail.enrollmentNo')}</span>
                                <span className="detail__value detail__value--mono">{fmt(s.enrollment_number)}</span>
                            </div>
                            <div className="detail__field">
                                <span className="detail__label">{t('studentDetail.rollNumber')}</span>
                                <span className="detail__value detail__value--num">{fmt(s.roll_number)}</span>
                            </div>
                            <div className="detail__field">
                                <span className="detail__label">{t('students.classSection')}</span>
                                <span className="detail__value">
                                    {s.current_class || s.current_section
                                        ? `${s.current_class?.name ?? ''}${s.current_section ? ' – ' + s.current_section.name : ''}`
                                        : <span className="detail__muted">{t('common.unassigned')}</span>}
                                </span>
                            </div>
                            <div className="detail__field">
                                <span className="detail__label">{t('studentDetail.admissionDate')}</span>
                                <span className="detail__value">{fmtDate(s.admission_date)}</span>
                            </div>
                            {s.school && (
                                <div className="detail__field detail__field--full">
                                    <span className="detail__label">{t('studentDetail.school')}</span>
                                    <span className="detail__value">
                                        {s.school.name}
                                        <span className="detail__value--mono detail__school-code"> · {s.school.school_code}</span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Personal */}
                    <section className="detail__section">
                        <h3 className="detail__section-title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                            </svg>
                            {t('studentDetail.personalDetails')}
                        </h3>
                        <div className="detail__grid">
                            <div className="detail__field">
                                <span className="detail__label">{t('addStudent.dateOfBirth')}</span>
                                <span className="detail__value">{fmtDate(u.date_of_birth)}</span>
                            </div>
                            <div className="detail__field">
                                <span className="detail__label">{t('addStudent.gender')}</span>
                                <span className="detail__value">{fmtGender(u.gender)}</span>
                            </div>
                            {u.email && (
                                <div className="detail__field detail__field--full">
                                    <span className="detail__label">{t('common.email')}</span>
                                    <span className="detail__value">
                                        <a href={`mailto:${u.email}`} className="detail__link">{u.email}</a>
                                    </span>
                                </div>
                            )}
                            {u.phone && (
                                <div className="detail__field">
                                    <span className="detail__label">{t('common.phone')}</span>
                                    <span className="detail__value">
                                        <a href={`tel:${u.phone}`} className="detail__link">{u.phone}</a>
                                    </span>
                                </div>
                            )}
                            {address && (
                                <div className="detail__field detail__field--full">
                                    <span className="detail__label">{t('common.address')}</span>
                                    <span className="detail__value">{address}</span>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Parents */}
                    {s.parents && s.parents.length > 0 && (
                        <section className="detail__section">
                            <h3 className="detail__section-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                                {t('addStudent.parentGuardian')}
                            </h3>
                            {s.parents.map((p) => (
                                <div key={p.id} className="detail__parent-card">
                                    <div className="detail__parent-header">
                                        <span className="detail__parent-name">
                                            {p.first_name}{p.last_name ? ' ' + p.last_name : ''}
                                        </span>
                                        <span className="detail__parent-meta">
                                            {p.relationship_type && (
                                                <span className="detail__parent-rel">{p.relationship_type}</span>
                                            )}
                                            {p.is_primary && (
                                                <span className="detail__parent-primary">{t('studentDetail.primary')}</span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="detail__grid">
                                        <div className="detail__field">
                                            <span className="detail__label">{t('common.phone')}</span>
                                            <span className="detail__value">
                                                {p.phone
                                                    ? <a href={`tel:${p.phone}`} className="detail__link">{p.phone}</a>
                                                    : dash}
                                            </span>
                                        </div>
                                        <div className="detail__field">
                                            <span className="detail__label">{t('common.email')}</span>
                                            <span className="detail__value">
                                                {p.email
                                                    ? <a href={`mailto:${p.email}`} className="detail__link">{p.email}</a>
                                                    : dash}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </section>
                    )}

                    {/* Metadata */}
                    {metaEntries.length > 0 && (
                        <section className="detail__section">
                            <h3 className="detail__section-title">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
                                </svg>
                                {t('studentDetail.additionalDetails')}
                            </h3>
                            <div className="detail__grid">
                                {metaEntries.map(([key, value]) => (
                                    <div className="detail__field" key={key}>
                                        <span className="detail__label">{key.replace(/_/g, ' ')}</span>
                                        <span className="detail__value">{String(value)}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Timestamps */}
                    {(s.created_at || s.updated_at) && (
                        <div className="detail__timestamps">
                            {s.created_at && (
                                <span className="detail__timestamp">
                                    {t('studentDetail.admitted', { date: new Date(s.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) })}
                                </span>
                            )}
                            {s.updated_at && (
                                <span className="detail__timestamp">
                                    {t('studentDetail.updated', { date: new Date(s.updated_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) })}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
